/**
 * @param url - the URL to fetch data from. The component will append the input value to this URL when making the request.
 * @param options - an optional array of options to display in the select dropdown. If provided, the component will use these options instead of fetching data from the URL.
 * @param value - the currently selected value. This should be an object with 'label' and 'value' properties.
 * @param accessor - an optional prop that allows you to specify how to extract the label and value from the fetched data.
 * If not provided, the component will assume that the fetched data has 'label' and 'value' properties.
 *
 *
 */

import AsyncSelect from "react-select/async";
import instance from "../../../../../Interceptor/api_instance";
import "./search-select.css";
import React from "react";

import { useTranslation } from "react-i18next";

const styles = {
  control: (provided: any) => ({
    ...provided,
    background: "var(--color-neutral-white)",
    border: "1px solid var(--color-secondary)",
    borderRadius: "16px",
    padding: "4px 16px",
    fontSize: "16px",
    minHeight: 0,
    height: "auto",
  }),

  container: (provided: any) => ({
    ...provided,
    color: "var(--color-neutral-gray)",
    position: "relative",
  }),
  menu: (provided: any) => ({
    ...provided,
    position: "absolute",
    zIndex: 30,
    backgroundColor: "var(--color-neutral-white)",
    boxShadow: "0px 0px 7px 0px rgba(207, 190, 255, 0.22)",
    borderRadius: "24px",
    padding: "8px 16px",
    marginTop: "10px",
    border: "1px solid var(--color-secondary)",
    color: "var(--color-neutral-gray)",
    minWidth: "fit-content",
    whiteSpace: "nowrap",
    overflow: "none"
  }),
  input: (provided: any) => ({
    ...provided,
    color: "var(--color-neutral-light-gray)",
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    minWidth: "100px",
    minHeight: 0,
    padding: 0,
  }),
  indicatorsContainer: (provided: any) => ({
    ...provided,
    minHeight: 0,
    padding: 0,
  }),
  placeholder: (provided: any) => ({
    ...provided,
    color: "var(--color-primary-light-blue)",
  }),
  option: (provided: any) => ({
    ...provided,
    borderBottom: "0.5px solid var(--color-secondary)",
    padding: "10px",
    color: "var(--color-neutral-gray)",
  }),
};

type AccessorOption<T> = {
  optionLabel: (row: T) => string;
  optionValue: (row: T) => string | number;
};

export type SearchOption = {
  label: string;
  value: string | number;
};

type BaseProps = Omit<
  React.InputHTMLAttributes<HTMLSelectElement>,
  "onChange" | "value"
> & {
  value: SearchOption | null;
  onChange: (selected: SearchOption | null) => void;
  accessor?: AccessorOption<any>;
  cacheOptions?: boolean;
};

// API-based search: provide a URL and optionally a debounce delay.
type APISearchProps = BaseProps & {
  url: string;
  debounceMs?: number;
  options?: never;
};

// Static search: provide an options array instead of fetching from an API.
type OptionsSearchProps = BaseProps & {
  options: Array<SearchOption>;
  url?: never;
  debounceMs?: never;
};

type SearchSelectProps = APISearchProps | OptionsSearchProps;

export const SearchSelectFilter = ({
  options,
  value,
  onChange,
  accessor,
  url,
  debounceMs = 700,
  ...props
}: SearchSelectProps) => {
  const { t } = useTranslation();

  const mapToOption = (item: any): SearchOption => ({
    label: accessor
      ? accessor.optionLabel(item)
      : String((item as any)?.label ?? ""),
    value: accessor ? accessor.optionValue(item) : ((item as any)?.value ?? ""),
  });

  const mappedOptions: SearchOption[] = (options ?? []).map(mapToOption);

  // Function to load options from the API based on the input value
  const loadOptions = async (inputValue: string) => {
    if (!inputValue || inputValue.length < 1) return [];
    try {
      const response = await instance.get(url + inputValue);
      return (response.data.results ?? (Array.isArray(response.data) ? response.data : [])).map((item: any) =>
        mapToOption(item),
      );
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  // Debounce the loadOptions function to avoid making too many requests while typing
  const delayLoadOptions = (inputValue: string) =>
    new Promise<SearchOption[]>((resolve) => {
      setTimeout(() => {
        loadOptions(inputValue).then(resolve);
      }, debounceMs);
    });

  const handleChange = (selected: SearchOption | null) => {
    if (!onChange) return;
    onChange(selected);
  };

  return (
      <AsyncSelect
        cacheOptions={props.cacheOptions ?? true}
        placeholder={props.placeholder ?? t("search")}
        styles={styles}
        loadOptions={delayLoadOptions}
        defaultOptions={mappedOptions}
        value={value}
        onChange={handleChange}
        menuPosition="absolute"
        isClearable
        unstyled={true}
        required
      />
  );
};